import React, { useState } from "react"
import tw from "twin.macro"
import { Input, InputType } from "../formElements/input"
import { Button, ButtonTheme } from "../formElements/button"
import { ReactComponent as CheckSVG } from "../../assets/icons/check.svg"
import { useTranslation } from "next-i18next"
import { useRequestPasswordMutation } from "graphql/generated/graphql"
import { PhoneNumberInput } from "../formElements/phoneInput"
import { validateEmail, validatePhoneNumber } from "../../utils/validations"
import { CountryData } from "react-phone-input-2"
import { TabState, Tabs } from "../authentication/join/signUp"
import { ResponseCode } from "../../graphql/generated/graphql"
import { useDebounce } from "../../utils/hooks/useDebounce"

export default function RequestPassword() {
  const [requestPasswordMutation] = useRequestPasswordMutation()
  const [email, setEmail] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [phoneCountryCode, setPhoneCountryCode] = useState("")
  const phoneWithoutCountry = phoneNumber.substring(phoneCountryCode.length)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [invalidEmail, setInvalidEmail] = useState("")
  const [invalidPhoneNumber, setInvalidPhoneNumber] = useState("")
  const [activeTab, setActiveTab] = useState<TabState>(TabState.Phone)
  const isEmail = activeTab == TabState.Email
  const isDisabled = isEmail ? !email || !!invalidEmail : !phoneNumber || !!invalidPhoneNumber
  const { t } = useTranslation("requestPassword")

  const clearInput = () => {
    setEmail("")
    setPhoneNumber("")
    setPhoneCountryCode("")
    setInvalidEmail("")
    setInvalidPhoneNumber("")
    setFormSubmitted(false)
  }
  const handleSubmit = async () => {
    const response = await requestPasswordMutation({ variables: { email, phoneNumber: phoneWithoutCountry } })
    switch (response.data?.RequestPassword?.code) {
      case ResponseCode.RequestFailedUserNotFound:
        isEmail ? setInvalidEmail(t("email_not_exist")) : setInvalidPhoneNumber(t("phone_not_exist"))
        break
      case ResponseCode.RequestSuccessful:
        setFormSubmitted(true)
        break
      default:
        break
    }
  }

  const validateEmailDebounce = useDebounce(validateEmail, 1000)
  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearInput()
    const { value: email } = e.target
    setEmail(email.toLowerCase().trim())
    validateEmailDebounce(email, setInvalidEmail)
  }

  const validatePhoneNumberDebounce = useDebounce(validatePhoneNumber, 1000)
  const handlePhoneNumber = (phone: string, country: unknown | CountryData) => {
    clearInput()
    setPhoneNumber(phone)
    setPhoneCountryCode((country as CountryData).dialCode)
    validatePhoneNumberDebounce(phone, setInvalidPhoneNumber)
  }

  return (
    <div tw="p-8 m-auto w-full tablet:w-[400px]">
      <h1 tw="title-3 w-full text-left mb-6">{t("h1")}</h1>
      <form>
        <div tw="my-8 max-w-lg">
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
          {isEmail ? (
            <Input
              label={t("input_email_label")}
              name="email"
              autoComplete="email"
              type={InputType.Email}
              inputValue={email}
              handleChange={handleEmail}
              invalid={!!invalidEmail}
              helpText={invalidEmail}
            />
          ) : (
            <PhoneNumberInput
              label={t("input_phone_label")}
              inputValue={phoneNumber}
              handleChange={handlePhoneNumber}
              error={invalidPhoneNumber}
              autoFocus={true}
            />
          )}
          <div tw="my-6">
            <Button
              onClickAsync={handleSubmit}
              theme={formSubmitted ? ButtonTheme.Success : undefined}
              action={formSubmitted ? t("button_submitted") : t("button")}
              icon={formSubmitted ? <CheckSVG /> : undefined}
              isDisabled={isDisabled || formSubmitted}
              twStyle={tw`w-full`}
            />
          </div>
        </div>
      </form>
    </div>
  )
}
